import { Link } from "react-router-dom";
import type { Film } from "@/types/film";

interface RelatedPanelProps {
  film: Film;
  related: Film[];
}

export default function RelatedPanel({ film, related }: RelatedPanelProps) {
  const listPath = film.kind === "movie" ? "/movies" : "/series";
  const listLabel = film.kind === "movie" ? "伦理电影" : "伦理电视剧";

  return (
    <aside className="lg:sticky lg:top-24">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-base font-semibold text-foreground-950">所属分类</h2>
        <div className="mt-3 flex flex-col gap-2">
          <Link
            to={listPath}
            className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className={film.kind === "movie" ? "ri-film-line" : "ri-tv-2-line"} />
              {listLabel}
            </span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </Link>
          <Link
            to="/reviews"
            className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-quill-pen-line" />
              影评与解析专题
            </span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </Link>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-heading text-base font-semibold text-foreground-950">精选影评</h2>
          <a href="#reviews" className="text-[11px] text-primary-600 hover:text-accent-600 cursor-pointer">
            查看全部
          </a>
        </div>
        {film.reviews.length === 0 ? (
          <p className="mt-3 rounded-md bg-background-100 px-3 py-5 text-center text-xs text-foreground-500">
            暂无影评
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {film.reviews.slice(0, 2).map((review) => (
              <li key={review.title}>
                <a href="#reviews" className="group block cursor-pointer">
                  <p className="text-xs font-medium leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-600">
                    {review.title}
                  </p>
                  <p className="mt-1 text-[11px] text-foreground-400">
                    {review.author} · {review.date}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-heading text-base font-semibold text-foreground-950">相关推荐</h2>
          <span className="text-[11px] text-foreground-400">类型 / 导演匹配</span>
        </div>

        {related.length === 0 ? (
          <p className="mt-4 rounded-md bg-background-100 px-3 py-6 text-center text-xs text-foreground-500">
            暂无相似作品
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-background-200">
            {related.map((item) => (
              <li key={item.id}>
                <Link to={`/detail/${item.id}`} className="group flex gap-3 py-3 cursor-pointer">
                  <div className="h-[72px] w-[54px] shrink-0 overflow-hidden rounded-sm bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title}封面`}
                      title={`${item.title} - 伦理视频相关推荐`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-heading text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-[11px] text-foreground-500">
                      {item.year} · {item.region}
                    </p>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                      <span className="flex items-center gap-0.5 text-accent-700">
                        <i className="ri-star-fill text-[10px]" />
                        {item.rating.toFixed(1)}
                      </span>
                      <span className="truncate text-foreground-400">{item.genres.join(" / ")}</span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 rounded-lg bg-primary-900 p-5 text-background-200">
        <h2 className="font-heading text-sm font-medium text-background-50">关于评分</h2>
        <p className="mt-2 text-[11px] leading-relaxed text-background-300">
          站内评分为演示数据，综合剧情完成度、人物塑造与伦理表达得出，仅供参考，不构成观看建议。
        </p>
      </div>
    </aside>
  );
}